import React, { forwardRef } from "react";
import { useField, useFormikContext } from "formik";
import { FileUpload } from "./FileUpload";
import type { FileUploadProps, FileUploadRef } from "./types/FileUpload.types";
import type { UploadedFile } from "./types/UploadedFile.type";

interface FormikFileUploadProps
	extends Omit<FileUploadProps, "onChange" | "initialValue"> {
	name: string;
}

/**
 * Formik-aware wrapper for FileUpload.
 *
 * Keeps `values[name]` in sync with the uploaded files list by mapping
 * the internal `UploadedFile` objects to the backend-expected shape:
 * `{ id, file_name, file_path, file_type, file_size }`.
 */
export const FormikFileUpload = forwardRef<FileUploadRef, FormikFileUploadProps>(
	({ name, ...props }, ref) => {
		const [field] = useField(name);
		const { setFieldValue } = useFormikContext();

		const handleChange = (files: UploadedFile[]) => {
			const mapped = files
				.filter((f) => f.id && (f.path || f.storage_path))
				.map((f) => ({
					id: f.id,
					file_name: f.name,
					file_path: f.path,
					file_type: f.type ?? null,
					file_size: f.size ?? 0,
					storage_path: f.storage_path ?? f.path,
				}));

			setFieldValue(name, mapped, false);
		};

		return (
			<FileUpload
				ref={ref}
				onChange={handleChange}
				{...props}
			/>
		);
	}
);

FormikFileUpload.displayName = "FormikFileUpload";
